import { gsap } from 'gsap';
import ScrollTrigger from 'gsap/dist/ScrollTrigger';
import css from './index.module.scss';
import { useGlobal } from '@/utils/useGlobal';
import { CanvasKeyFrames } from '@/utils/canvaskeyframes';
import { getImageSpec } from '@/utils/locals';
import { createEFPic } from '@/utils/useEFPic';
import { handleDynamicPath } from '@/utils/handleDynamicPath';

const { EFPic } = createEFPic('/media/deltapro/pc/');
const { EFPic: EFPic2 } = createEFPic('');
gsap.registerPlugin(ScrollTrigger);

export default function Section8() {
    const { useEffect, useLang, useRouter, Link } = useGlobal();
    const { locale } = useRouter();
    const imgSpec = getImageSpec(locale, ['uk', 'au', 'jp', 'cn', 'de', 'fr', 'es', 'it', 'eu', 'za']); // delta pro有日规
    const { t } = useLang('delta_pro_new');

    const createImgs = () => {
        const imageEleArr: any[] = [];
        const imageCount = 50;
        return new Promise((resolve, reject) => {
            let loadImageCount = 0;
            for (let i = 0; i < imageCount; i++) {
                const imageEle = new window.Image();
                imageEleArr.push(imageEle);
                imageEle.onload = function () {
                    loadImageCount++;
                    if (loadImageCount >= imageCount) {
                        // 此时图片全部加载完成
                        resolve(imageEleArr);
                    }
                };
                imageEle.onerror = reject;

                imageEle.src = handleDynamicPath(`/media/deltapro/pc/line/line_000${i < 10 ? `0${i}` : i}.png`);
            }
        });
    };

    useEffect(() => {
        /* -start- 家庭充电动画 */
        const imageCount = 50;

        const TL = gsap.timeline({
            scrollTrigger: {
                trigger: '.section8',
                scrub: 0.5,
                start: 'top top',
                end: `+=200%`,
            },
        });
        // 提前加载序列帧所需图片
        let canvasKeyFramesInstance: any = null;
        const TLPreload = gsap.timeline({
            scrollTrigger: {
                trigger: '.section8 .p1',
                start: 'top bottom-=500',
                once: true,
                onEnter() {
                    const container = document.querySelector('.img_2.animation');
                    if (!container) return;
                    createImgs().then((imgArr) => {
                        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                        // @ts-ignore
                        canvasKeyFramesInstance = new CanvasKeyFrames(container, 'array', imgArr, {
                            width: 840,
                            height: 500,
                            fps: 30,
                        });
                    });
                },
            },
        });

        // 充电电流
        let preFrame: number;
        TL.to('.section8 .img_2', {
            duration: 0.5,
            onUpdate() {
                const progress = this.progress();
                const currentFrame = Math.floor(progress * (imageCount - 1));
                if (preFrame === currentFrame) return;
                canvasKeyFramesInstance && canvasKeyFramesInstance.goto(currentFrame);
            },
        });
        TL.to('.section8 .img_3', {
            duration: 0.5,
            opacity: 1,
        });

        // p1 -> p2转场 hide
        TL.to('.section8 .p1', {
            opacity: 0,
            duration: 1,
            ease: 'power3.in',
        });
        TL.to(
            '.section8 .p1 .left',
            {
                y: -200,
                duration: 1,
                ease: 'power3.in',
            },
            '-=1',
        );

        // show
        TL.to('.section8 .p2', {
            opacity: 1,
            duration: 1,
            ease: 'power3.out',
        });
        TL.to(
            '.section8 .p2 .left',
            {
                y: 0,
                duration: 1,
                ease: 'power3.out',
            },
            '-=1',
        );

        /* -end- 家庭充电动画 */
        return () => {
            TL.kill();
            TLPreload.kill();
        };
    }, []);

    return (
        <div className={`${css.section8} sec8-sticky-wrapper GtmModuleData-section`} data-section-id={css.section8} data-section-title={t('AC_output_desc')}>
            <div className="sec8-sticky-container">
                <div className="sec8-container">
                    <div className="p1">
                        <div className="left">
                            <div className="line1 font-family-semibolder">{t('AC_output_desc')}</div>
                            <div className="line2 font-family-regular">
                                {t.rich('AC_output_notice', {
                                    line1: (text: string) => (
                                        <Link className="ef-link__page" href="/x-boost" newWin>
                                            {text}
                                        </Link>
                                    ),
                                    div: (text: string) => <div>{text}</div>,
                                })}
                            </div>
                        </div>
                        <div className="right">
                            {locale === 'jp' ? (
                                <>
                                    <div className="img_1">
                                        <div className="imgBox">
                                            <EFPic name={[`indoor.webp`, `indoor.jpg`]} alt="" />
                                        </div>
                                    </div>
                                </>
                            ) : (
                                <>
                                    <div className="img_1">
                                        <div className="imgBox">
                                            {locale === 'ph' ? (
                                                <EFPic2 name="https://websiteoss.ecoflow.com/upload/image/1751093366922/2.png" alt="" />
                                            ) : (
                                                <EFPic name={[`section_8_1_${imgSpec}.webp`, `section_8_1_${imgSpec}.jpg`]} alt="" />
                                            )}
                                        </div>
                                    </div>
                                    <div className="img_2 animation"></div>
                                    <div className="img_3">
                                        <div className="imgBox">
                                            {locale === 'ph' ? (
                                                <EFPic2 name="https://websiteoss.ecoflow.com/upload/image/1751093366922/2.png" alt="" />
                                            ) : (
                                                <EFPic name={['section_8_mask.webp', 'section_8_mask.png']} alt="" />
                                            )}
                                        </div>
                                    </div>
                                </>
                            )}
                        </div>
                    </div>
                    <div className="p2">
                        <div className="left">
                            <div className="line1 font-family-semibolder">{t('AC_output_desc2')}</div>
                        </div>
                        <div className="right">
                            <div className="img_1">
                                <div className="imgBox">
                                    {locale === 'jp' ? (
                                        <EFPic name={[`outdoor.webp`, `outdoor.jpg`]} alt="" />
                                    ) : locale === 'ph' ? (
                                        <EFPic2 name="https://websiteoss.ecoflow.com/upload/image/1751093366571/1.png" alt="" />
                                    ) : (
                                        <EFPic name={[`section_8_2_${imgSpec}.webp`, `section_8_2_${imgSpec}.jpg`]} alt="" />
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
